import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import Link from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import { useCompanyPageStore } from "../../../../store/companyPageStore";
import { transformCompanyPageFiltersToExportParams } from "./transformCompanyPageFiltersToExportParams";
import { getLoggedUserProfile } from "../../../../utils/auth";
import {
  RowCountData,
  useCompanyProfileRowCount,
} from "../../hooks/useCompanyProfileRowCount";
import {
  ExportFailureReason,
  INSTALLS_DATA_POINTS_LIMIT,
  EXPORT_MESSAGES,
} from "./constants";
import {
  getExportResults,
  NifflerExportError,
  NIFFLER_ERROR_MESSAGES,
  NifflerErrorCode,
} from "../../../../apis/niffler/export";
import { useSnackbar } from "../../../../components/Snackbar";
import { ExportLoadingBackdrop } from "./ExportLoadingBackdrop";
import { ExportErrorDialog } from "./ExportErrorDialog";
import { useExportDialog } from "./useExportDialog";
import { useHasCreditsEnforcement } from "../../../../hooks/useHasCreditsEnforcement";
import {
  estimateExportCredits,
  EstimateCreditsDetailResponse,
} from "../../../../apis/exportTrackingApi";
import { ExportCreditsAlert } from "../../../../components/Export/ExportCreditsAlert";

interface CompanyExportDialogProps {
  open: boolean;
  onClose: () => void;
}

const validateRowCount = (rowCountData: RowCountData) => {
  if (rowCountData.installsRowCount >= INSTALLS_DATA_POINTS_LIMIT) {
    return {
      isValidExport: false,
      failureReason: ExportFailureReason.FILE_TOO_LARGE_INSTALL,
    };
  }

  return {
    isValidExport: true,
    failureReason: "" as ExportFailureReason,
  };
};

export const CompanyExportDialog: React.FC<CompanyExportDialogProps> = ({
  open,
  onClose,
}) => {
  const company = useCompanyPageStore((state) => state.company);
  const installFilters = useCompanyPageStore((state) => state.installFilters);
  const intentFilters = useCompanyPageStore((state) => state.intentFilters);
  const userProfile = getLoggedUserProfile();
  const userEmail = userProfile?.email;
  const { showSuccess } = useSnackbar();
  const hasCreditsEnforcement = useHasCreditsEnforcement();
  const [estimate, setEstimate] =
    useState<EstimateCreditsDetailResponse | null>(null);
  const [isEstimatingCredits, setIsEstimatingCredits] =
    useState<boolean>(false);
  const [creditsError, setCreditsError] = useState<boolean>(false);

  const {
    loading,
    error,
    showLoading,
    hideLoading,
    showError,
    hideError,
    updateLoadingMessage,
    resetAll,
  } = useExportDialog();

  const { fetchRowCountData } = useCompanyProfileRowCount({
    hgId: company!.hgId,
    install: installFilters,
    intent: intentFilters,
  });

  const exportParams = useMemo(
    () =>
      transformCompanyPageFiltersToExportParams(userEmail!, company!, {
        installFilters,
        intentFilters,
      }),
    [userEmail, company, installFilters, intentFilters]
  );

  // Estimate credits when dialog opens and credits enforcement is enabled
  useEffect(() => {
    if (open && hasCreditsEnforcement) {
      const estimateCredits = async () => {
        setIsEstimatingCredits(true);
        setCreditsError(false); // Reset error state
        try {
          const result = await estimateExportCredits({
            export_payload: exportParams,
          });
          setEstimate(result);
        } catch (error) {
          console.error("Failed to estimate export credits:", error);
          setCreditsError(true);
        } finally {
          setIsEstimatingCredits(false);
        }
      };

      estimateCredits();
    }
  }, [open, hasCreditsEnforcement, exportParams]);

  const handleExport = async () => {
    showLoading(EXPORT_MESSAGES.LOADING.VALIDATING);

    try {
      const rowCountData = await fetchRowCountData();
      const { isValidExport, failureReason } = validateRowCount(rowCountData);

      if (!isValidExport) {
        hideLoading();

        if (failureReason === ExportFailureReason.FILE_TOO_LARGE_INSTALL) {
          showError(
            EXPORT_MESSAGES.ERROR.TITLE,
            EXPORT_MESSAGES.ERROR.FILE_TOO_LARGE
          );
          return;
        }

        showError(EXPORT_MESSAGES.ERROR.TITLE, EXPORT_MESSAGES.ERROR.GENERIC);
        return;
      }

      // Update loading dialog for processing
      updateLoadingMessage(
        EXPORT_MESSAGES.LOADING.PROCESSING,
        EXPORT_MESSAGES.LOADING.PROCESSING_SUBTITLE
      );

      await getExportResults(exportParams);
      hideLoading();

      // Show success notification
      showSuccess(EXPORT_MESSAGES.SUCCESS.getMessage(userEmail!), 10000);

      resetAll();
      onClose();
    } catch (error: unknown) {
      hideLoading();

      if (error instanceof NifflerExportError) {
        const errorConfig =
          NIFFLER_ERROR_MESSAGES[error.code as NifflerErrorCode];

        if (errorConfig) {
          showError(errorConfig.title, errorConfig.message);
        } else {
          showError(EXPORT_MESSAGES.ERROR.TITLE, EXPORT_MESSAGES.ERROR.GENERIC);
        }
      } else {
        showError(EXPORT_MESSAGES.ERROR.TITLE, EXPORT_MESSAGES.ERROR.GENERIC);
      }
    }
  };

  return (
    <>
      <Dialog
        open={open && !loading.visible && !error.visible}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5">{EXPORT_MESSAGES.DIALOG.TITLE}</Typography>
            <IconButton
              size="small"
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          {hasCreditsEnforcement && (
            <ExportCreditsAlert
              estimate={estimate}
              isLoading={isEstimatingCredits}
              error={creditsError}
              sx={{ mb: 2 }}
            />
          )}
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {EXPORT_MESSAGES.DIALOG.DESCRIPTION}{" "}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {EXPORT_MESSAGES.DIALOG.SUPPORT_TEXT}{" "}
            <Link
              href={`mailto:${EXPORT_MESSAGES.DIALOG.SUPPORT_EMAIL}`}
              underline="hover"
            >
              {EXPORT_MESSAGES.DIALOG.SUPPORT_EMAIL}
            </Link>
            .
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
            size="small"
          >
            {EXPORT_MESSAGES.BUTTONS.CANCEL}
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            size="small"
            disabled={isEstimatingCredits}
          >
            {EXPORT_MESSAGES.BUTTONS.EXPORT}
          </Button>
        </DialogActions>
      </Dialog>

      <ExportLoadingBackdrop
        open={loading.visible}
        title={loading.title}
        subtitle={loading.subtitle}
      />

      <ExportErrorDialog
        open={error.visible}
        onClose={hideError}
        title={error.title}
        message={error.message}
      />
    </>
  );
};
